import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { Workflow } from "lucide-react";
import { useState } from "react";
import { useNavigate } from "react-router";
import { DataTable, EmptyState, formatRelative, PageHeader, Pagination, Panel, StatusBadge } from "@hostdock/ui";
import { api } from "../../api";
import { RequirePermission } from "../../lib/guard";

const PAGE_SIZE = 25;

export function ProvisioningPage() {
  return (
    <RequirePermission anyOf={["INFRASTRUCTURE_VIEW"]}>
      <ProvisioningList />
    </RequirePermission>
  );
}

function ProvisioningList() {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const q = useQuery({ queryKey: ["provisioning", page], queryFn: () => api.provisioning.list({ page, pageSize: PAGE_SIZE }), placeholderData: keepPreviousData, refetchInterval: 5000 });
  return (
    <>
      <PageHeader title="Provisioning" description="Every provisioning and component-update run across all servers." />
      <Panel flush>
        <DataTable
          rows={q.data?.items}
          loading={q.isPending}
          error={q.error}
          onRetry={() => void q.refetch()}
          rowKey={(r) => r.id}
          onRowClick={(r) => navigate(`/infrastructure/servers/${r.server.id}?tab=provisioning`)}
          empty={<EmptyState icon={Workflow} title="No provisioning runs yet" description="Runs appear when you add a server or update one." />}
          columns={[
            { key: "server", header: "Server", cell: (r) => <span className="font-mono text-[13px] font-medium">{r.server.name}</span> },
            { key: "kind", header: "Run", cell: (r) => (r.kind === "UPDATE" ? "Component update" : "Provisioning") },
            { key: "status", header: "Status", cell: (r) => <StatusBadge status={r.status} /> },
            {
              key: "progress",
              header: "Progress",
              cell: (r) => (
                <div className="min-w-32">
                  <p className="text-[12px] tabular-nums">{r.progress.done}/{r.progress.total}{r.progress.current ? ` · ${r.progress.current}` : ""}</p>
                  <div className="mt-0.5 h-1 overflow-hidden rounded-full bg-surface-3">
                    <div className={`h-full ${r.status === "FAILED" ? "bg-danger" : "bg-accent"}`} style={{ width: `${(r.progress.done / r.progress.total) * 100}%` }} />
                  </div>
                </div>
              ),
            },
            { key: "started", header: "Started", cell: (r) => <span className="text-muted">{r.startedAt ? formatRelative(r.startedAt) : "Queued"}</span>, hideOnMobile: true },
            { key: "error", header: "Error", cell: (r) => <span className="line-clamp-1 text-[12px] text-danger">{r.error ?? ""}</span>, hideOnMobile: true },
          ]}
        />
        {q.data && <Pagination page={page} pageSize={PAGE_SIZE} total={q.data.total} onPage={setPage} />}
      </Panel>
    </>
  );
}
